import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Users, Layers } from 'lucide-react';
import { whatsappApi, WhatsAppChannel } from '../../../services/whatsappApi';
import ChannelNotConfigured from './ChannelNotConfigured';
import WhatsAppChannelStatus from './WhatsAppChannelStatus';
import WhatsAppRosterTable from './WhatsAppRosterTable';
import WorkflowsPanel from './workflows/WorkflowsPanel';

interface WhatsAppDeploymentPanelProps {
  clientId: string;
  /** Kept for back-compat with any caller that still passes it; not used for gating. */
  userRole?: string;
  /** Client profile snapshot (used to personalize default copy in the setup form). */
  businessName?: string;
  botName?: string;
  /** Told the channel (or null) after every load, so a summary elsewhere on the
   *  page (the Channels tile) follows setup, pause and resume made in here. */
  onChannelLoaded?: (channel: WhatsAppChannel | null) => void;
}

export const WhatsAppDeploymentPanel: React.FC<WhatsAppDeploymentPanelProps> = ({
  clientId,
  businessName,
  botName,
  onChannelLoaded,
}) => {
  const [channel, setChannel] = useState<WhatsAppChannel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Authorization is enforced server-side. Any user who can see this client's
  // marketplace can provision their own WhatsApp channel — business owners
  // shouldn't need to escalate to a consultant for self-serve setup.

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const channels = await whatsappApi.listChannels(clientId);
      const wa = channels.find((c) => c.channel_type === 'whatsapp') || null;
      setChannel(wa);
      onChannelLoaded?.(wa);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load channel');
    } finally {
      setLoading(false);
    }
  }, [clientId, onChannelLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
        <Loader2 size={18} className="animate-spin inline-block mr-2" />
        Loading WhatsApp configuration…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!channel) {
    return (
      <ChannelNotConfigured
        clientId={clientId}
        businessName={businessName}
        botName={botName}
        onCreated={load}
      />
    );
  }

  return (
    <div className="space-y-5">
      <WhatsAppChannelStatus clientId={clientId} channel={channel} onChange={load} />
      <DeploymentTabs clientId={clientId} channelId={channel.id} />
    </div>
  );
};

type Tab = 'recipients' | 'workflows';

const DeploymentTabs: React.FC<{ clientId: string; channelId: string }> = ({ clientId, channelId }) => {
  const [tab, setTab] = useState<Tab>('recipients');
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-200">
        <TabButton active={tab === 'recipients'} onClick={() => setTab('recipients')} icon={<Users size={14} />}>
          Recipients
        </TabButton>
        <TabButton active={tab === 'workflows'} onClick={() => setTab('workflows')} icon={<Layers size={14} />}>
          Workflows
        </TabButton>
      </div>

      {tab === 'recipients' && (
        <WhatsAppRosterTable clientId={clientId} channelId={channelId} />
      )}
      {tab === 'workflows' && <WorkflowsPanel clientId={clientId} />}
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ active, onClick, icon, children }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active
        ? 'border-emerald-500 text-emerald-700'
        : 'border-transparent text-gray-500 hover:text-gray-800'
    }`}
  >
    {icon}
    {children}
  </button>
);

export default WhatsAppDeploymentPanel;
